/* =========================================================================
   Routinae — the corner question panel: interface layer, both modes.

   Rendered by snippets/routina-chat.liquid, which decides the mode from one
   theme setting and writes it into the config as "mode":

     "faq"  the endpoint is blank. A list of written questions; tapping one
            moves an answer the theme already translated into the log. This
            file makes NO NETWORK REQUEST AT ALL in this mode — there is no
            endpoint to call and nothing to send. It renders on every locale
            because there is nothing to disclose.

     "ai"   the endpoint is set and the visitor is on English. Free text,
            answered by ai/chat.js on a Cloudflare Worker. Unchanged.

   Both modes share the shell, the message rendering, the link chips and the
   focus handling, which is the point: a visitor who meets FAQ mode today and
   AI mode later meets the same panel twice.

   The rules this file lives by, in order:

   1. NO innerHTML PATH EXISTS IN THIS FILE. Anything the model wrote, and
      anything the visitor wrote, and every FAQ answer, can only ever become
      a text node. The worker filters model output before it gets here
      (ai/chat.js → chatViolates), but a filter is a control at a distance;
      this is the control in hand.

   2. Links are never model text. The model returns a token ("finder",
      "contact", ...); the widget maps it to a URL that Liquid wrote, in the
      visitor's own locale, or ignores it. An unknown token renders nothing.
      FAQ answers carry their token in an attribute Liquid wrote, and go
      through the same map.

   3. There is no error state. If the endpoint is unreachable, slow, or
      answers with any `skipped` payload, the visitor sees one calm fallback
      message pointing at the routine finder and the contact page — both of
      which work without this widget existing. Nothing red, nothing broken,
      no console noise. FAQ mode has nothing that can fail: the answers are
      already in the page before the visitor opens it.

   4. The conversation lives in this closure and nowhere else. Nothing is
      written to localStorage, sessionStorage or a cookie; closing the tab
      is deletion. In AI mode the request carries the locale and the visible
      conversation only, with credentials omitted. In FAQ mode there is no
      request.

   Accessibility: the toggle is a real <button> with aria-expanded and a
   visually hidden label; the panel is a labelled <section>; the message log
   is role="log" with aria-live="polite" so answers are announced without
   stealing focus; the FAQ questions are real <button>s in a labelled list,
   so they are reachable and announced as a list; Escape closes the panel and
   focus returns to the toggle; prefers-reduced-motion is honoured in the CSS.
   ========================================================================= */

(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-rtchat-root]');
    if (!root) return;

    /* Config, written by Liquid. If it is missing or unparseable, or (in AI
       mode) this browser lacks fetch/AbortController, the widget simply never
       appears — a shop must not wear a broken chat button. */
    var cfg = null;
    try {
      var cfgEl = root.querySelector('[data-rtchat-config]');
      cfg = cfgEl ? JSON.parse(cfgEl.textContent) : null;
    } catch (e) { cfg = null; }

    if (!cfg) {
      root.hidden = true;
      return;
    }

    /* The mode is what Liquid rendered, not what the config claims: FAQ mode
       needs the question list to exist. A config saying "faq" against AI
       markup, or the reverse, falls back to the older behaviour — endpoint
       present or the widget removes itself. */
    var faqWrap = root.querySelector('[data-rtchat-faq]');
    var mode = cfg.mode === 'faq' && faqWrap ? 'faq' : 'ai';

    if (mode === 'ai' && (!cfg.endpoint || !window.fetch || !window.AbortController)) {
      root.hidden = true;
      return;
    }

    var toggle = root.querySelector('[data-rtchat-toggle]');
    var panel  = root.querySelector('[data-rtchat-panel]');
    var log    = root.querySelector('[data-rtchat-log]');
    var form   = root.querySelector('[data-rtchat-form]');
    var input  = root.querySelector('[data-rtchat-input]');
    var send   = root.querySelector('[data-rtchat-send]');
    var typing = root.querySelector('[data-rtchat-typing]');

    if (!toggle || !panel || !log) {
      root.hidden = true;
      return;
    }
    if (mode === 'ai' && (!form || !input || !send)) {
      root.hidden = true;
      return;
    }

    /* The strings the JS needs beyond what Liquid already rendered: only the
       fallback text, which Liquid parks in a data attribute via the config.
       Everything visible at load time is server-rendered and translated, and
       so is every FAQ answer — this file writes no copy of its own. */
    var links = cfg.links || {};

    var history = [];      // [{role, content}] — memory only, never stored.
    var pending = false;
    var MAX_SENT = 6;      // must match CHAT_MAX_TURNS in ai/chat.js.
    var MAX_CHARS = 500;   // must match CHAT_MAX_USER_CHARS in ai/chat.js.

    root.hidden = false;

    /* ------------------------------------------------------------------ */
    /* rendering — text nodes only                                         */
    /* ------------------------------------------------------------------ */

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function bubble(who) {
      return el('div', 'rtchat__msg rtchat__msg--' + who);
    }

    /* Plain text in, paragraphs of text nodes out. Blank lines split. */
    function paras(parent, text) {
      var parts = String(text).split(/\n{2,}/);
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i].replace(/\s+/g, ' ').trim();
        if (p) parent.appendChild(el('p', null, p));
      }
    }

    /* A link chip. The href comes from cfg.links — written by Liquid, never
       by the model. An unknown token renders nothing at all. */
    function chip(parent, token) {
      var target = token && token !== 'none' ? links[token] : null;
      if (!target || typeof target.url !== 'string' || typeof target.label !== 'string') return;
      var a = el('a', 'rtchat__chip', target.label);
      a.setAttribute('href', target.url);
      parent.appendChild(a);
    }

    function scrollDown() {
      try { log.scrollTop = log.scrollHeight; } catch (e) { /* stub DOMs */ }
    }

    function addUser(text) {
      var b = bubble('user');
      paras(b, text);
      log.appendChild(b);
      scrollDown();
    }

    function addBot(text, linkToken) {
      var b = bubble('bot');
      paras(b, text);
      chip(b, linkToken);
      log.appendChild(b);
      scrollDown();
      return b;
    }

    /* ------------------------------------------------------------------ */
    /* open / close                                                        */
    /* ------------------------------------------------------------------ */

    var firstQuestion = mode === 'faq' ? root.querySelector('[data-rtchat-q]') : null;

    function setOpen(open) {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        /* Focus the thing the visitor is meant to use: the composer in AI
           mode, the first question in FAQ mode. */
        if (mode === 'ai') input.focus();
        else if (firstQuestion) firstQuestion.focus();
        scrollDown();
      } else {
        toggle.focus();
      }
    }

    toggle.addEventListener('click', function () {
      setOpen(panel.hidden);
    });

    var closeBtn = root.querySelector('[data-rtchat-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { setOpen(false); });

    document.addEventListener('keydown', function (e) {
      if (!panel.hidden && (e.key === 'Escape' || e.key === 'Esc')) setOpen(false);
    });

    /* ------------------------------------------------------------------ */
    /* FAQ mode — everything below happens in the page, and only there      */
    /* ------------------------------------------------------------------ */

    if (mode === 'faq') {
      /* Tapping the same question twice in a row would stack two identical
         pairs of bubbles, which reads as a bug rather than as an answer. The
         second tap scrolls to the answer already standing. */
      var lastAsked = null;

      var answer = function (btn) {
        var id = btn.getAttribute('data-rtchat-q');
        if (id && id === lastAsked) {
          scrollDown();
          return;
        }
        lastAsked = id;
        addUser(btn.textContent);
        addBot(btn.getAttribute('data-rtchat-a') || '', btn.getAttribute('data-rtchat-link'));
      };

      var wire = function (btn) {
        btn.addEventListener('click', function () { answer(btn); });
      };

      var questions = root.querySelectorAll('[data-rtchat-q]');
      for (var qi = 0; qi < questions.length; qi++) wire(questions[qi]);

      return; // no network path exists past this point.
    }

    /* ------------------------------------------------------------------ */
    /* AI mode — sending                                                   */
    /* ------------------------------------------------------------------ */

    /* The one thing shown when anything at all goes wrong. Rendered at most
       once per conversation — a second failure just leaves it standing. */
    var fellBack = false;
    function fallback() {
      if (fellBack) return;
      fellBack = true;
      var b = bubble('bot');
      b.setAttribute('data-rtchat-fallback', '');
      paras(b, cfg.fallbackText || root.getAttribute('data-rtchat-fallback-text') || '');
      chip(b, 'finder');
      chip(b, 'contact');
      log.appendChild(b);
      scrollDown();
    }

    function setPending(on) {
      pending = on;
      send.disabled = on;
      if (typing) typing.hidden = !on;
    }

    function ask(text) {
      history.push({ role: 'user', content: text });
      addUser(text);
      setPending(true);

      var ctrl = new AbortController();
      var done = false;
      var timer = window.setTimeout(function () { if (!done) ctrl.abort(); }, cfg.timeout || 10000);

      fetch(cfg.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locale: cfg.locale || 'en',
          messages: history.slice(-MAX_SENT)
        }),
        signal: ctrl.signal,
        credentials: 'omit'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          done = true;
          window.clearTimeout(timer);
          setPending(false);
          if (data && typeof data.reply === 'string' && data.reply) {
            history.push({ role: 'assistant', content: data.reply });
            addBot(data.reply, data.link);
          } else {
            // Every `skipped` payload, a non-JSON body and a null all land
            // here — deliberately indistinguishable to the visitor.
            fallback();
          }
        })
        .catch(function () {
          done = true;
          window.clearTimeout(timer);
          setPending(false);
          fallback();
        });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (pending) return;
      var text = String(input.value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_CHARS);
      if (!text) return;
      input.value = '';
      ask(text);
    });

    /* Enter sends, Shift+Enter makes a newline — the convention every chat
       window has taught. Guarded so a stub DOM without key events is fine. */
    input.addEventListener('keydown', function (e) {
      if (e && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        form.dispatchEvent ? form.dispatchEvent(new Event('submit', { cancelable: true })) : form.dispatch('submit');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
